import React, { useCallback, useEffect, useState } from 'react'
import DashboardLayout from '../../../../../layoutcontainers/DashboardLayout'
import { AppBar, Box, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, List, ListItem, ListItemText, Toolbar } from '@mui/material'
import MaterialTypography from '../../../../../components/MaterialTypography'
import MaterialButton from '../../../../../components/MaterialButton'
import { Grid } from '@mui/system'
import MaterialBox from '../../../../../components/MaterialBox'
import MaterialSelect from '../../../../../components/MaterialSelect'
import MaterialInput from '../../../../../components/MaterialInput'
import './createTestRun.css'
import { fetchData, postData, putData } from '../../../../Utils/Baseurl'
import { useLoading } from '../../../../../context/loadingcontext'
import { useLocation, useNavigate } from 'react-router-dom'
import { getProjectsMiddleware } from '../../../../../api/projectMiddleware'


const CreateTestRun = () => {
    const { setCirleLoading } = useLoading();
    const navigate = useNavigate();
    const location = useLocation();
    const editData = location.state;
    const [testRun, setTestRun] = useState({
        project: '',
        testRunName: '',
        testCases: '',
        testPlan: '',
        description: '',
        assignRun: '',
        state: ''
    });
    const [open, setOpen] = useState(false);
    const [selectedLeftIds, setSelectedLeftIds] = useState([]);
    const [selectedRightIds, setSelectedRightIds] = useState([]);
    const [projectList, setProjectList] = useState([]);
    const [projectID, setProjectID] = useState('');
    const [testPlanList, setTestPlanList] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);
    const [assignOptions, setAssignOptions] = useState([]);
    const [moduleList, setModuleList] = useState([]);
    const [moduleTestCases, setModuleTestCases] = useState({});


    useEffect(() => {
        fetchProjects();
        fetchStateOptions();
    }, []);

    useEffect(() => {
        if (editData?.id) {
            fetchTestRunDetails(editData.id);
        }
    }, [editData]);

    const fetchTestRunDetails = async (id) => {
        setCirleLoading(true);
        try {
            const response = await fetchData(`api/testrun/${id}/`);
            if (response && response.data) {
                const data = response.data;
                setProjectID(data.project_id);

                const testCases = data.test_cases || [];
                // Use Number() to ensure type consistency (integer vs string)
                const tcIds = testCases.map(tc => typeof tc === 'object' ? Number(tc.id) : Number(tc));
                setSelectedRightIds(tcIds);

                // Extract module IDs for pre-selection using folder_id
                const folderIds = [...new Set(testCases
                    .filter(tc => typeof tc === 'object' && tc.folder_id)
                    .map(tc => Number(tc.folder_id))
                )];

                if (folderIds.length > 0) {
                    setSelectedLeftIds(folderIds);
                    folderIds.forEach(fId => {
                        fetchTestCaseList(fId);
                    });
                }

                setTestRun({
                    project: { value: data.project_id, label: data.project_name || '' },
                    testRunName: data.name,
                    testCases: `Test Cases : ${tcIds.length}`,
                    testPlan: { value: data.test_plan_id, label: data.test_plan_title || '' },
                    description: data.description,
                    assignRun: { value: data.assign_id, label: data.assign_name || '' },
                    state: { value: data.status, label: data.status }
                });
            }
        } catch (error) {
            console.error("Error fetching test run details:", error);
        } finally {
            setCirleLoading(false);
        }
    }

    useEffect(() => {
        fetchTestPlans(projectID);
    }, [projectID]);

    useEffect(() => {
        if (projectID) {
            fetchAssignOptions(projectID);
        }
    }, [projectID]);

    useEffect(() => {
        if (projectID) {
            fetchProjectModules();
        }
    }, [projectID]);

    const fetchProjects = useCallback(async () => {
        setCirleLoading(true);
        try {
            const userId = sessionStorage.getItem("currentUser");
            if (userId) {
                const response = await getProjectsMiddleware(userId);
                if (response && response.data) {
                    const formattedProjects = response.data.map(proj => ({
                        value: proj.project_id,
                        label: proj.project_name
                    }));
                    setProjectList(formattedProjects);
                }
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setCirleLoading(false);
        }
    }, [setCirleLoading]);

    const fetchTestPlans = useCallback(async (projectId) => {
        setCirleLoading(true);
        try {
            if (projectId) {
                const response = await fetchData(`api/testplan/?project_id=${projectId}`);
                if (response && response.data) {
                    const formattedPlans = response.data.map(plan => ({
                        value: plan.id,
                        label: plan.title
                    }));
                    setTestPlanList(formattedPlans);
                }
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setCirleLoading(false);
        }
    }, [setCirleLoading]);

    const fetchStateOptions = useCallback(async () => {
        setCirleLoading(true);
        try {
            const response = await fetchData(`api/testrun_choices/`);
            setStateOptions(response.state);
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setCirleLoading(false);
        }
    }, [setCirleLoading])

    const fetchAssignOptions = useCallback(async (projectId) => {
        if (!projectId) return;

        setCirleLoading(true);
        try {
            const response = await fetchData(`api/assign_run/${projectId}/`);
            const formattedOptions = response.data.map(assign => ({
                value: assign.id,
                label: assign.employee_name
            }))
            setAssignOptions(formattedOptions);
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setCirleLoading(false);
        }
    }, [setCirleLoading])

    const fetchProjectModules = async () => {
        if (!projectID) return;
        setCirleLoading(true);
        try {
            const response = await fetchData(`api/get_project_folders/${projectID}/?flag=1`);
            const formattedOptions = response.data.map(module => ({
                value: Number(module.id),
                label: module.module_name,
                id: Number(module.id)
            }))
            setModuleList(formattedOptions);
        } catch (error) {
            console.error("Error fetching projects:", error);
        } finally {
            setCirleLoading(false);
        }
    }

    const fetchTestCaseList = async (folderId) => {
        setCirleLoading(true);
        try {
            const formData = new FormData();
            formData.append('folder_id', folderId);
            formData.append('flag', 1);

            const response = await postData('api/get_testcases/', formData);

            if (response && response.data) {
                const formattedTestCases = response.data.map(tc => ({
                    id: Number(tc.id),
                    name: tc.title || tc.testcase_title || tc.name
                }));

                setModuleTestCases(prev => ({
                    ...prev,
                    [folderId]: formattedTestCases
                }));
            }

        } catch (error) {
            console.error("Error fetching test cases:", error);
        } finally {
            setCirleLoading(false);
        }
    }


    const filteredRightList = selectedLeftIds.length
        ? selectedLeftIds.flatMap((id) => moduleTestCases[id] || [])
        : [];

    const handleLeftCheck = async (id) => {
        const isSelected = selectedLeftIds.includes(id);

        if (!isSelected) {
            await fetchTestCaseList(id);
            setSelectedLeftIds((prev) => [...prev, id]);
        } else {
            setSelectedLeftIds((prev) => prev.filter((item) => item !== id));
            const relatedIds = (moduleTestCases[id] || []).map((r) => r.id);
            setSelectedRightIds((prev) => prev.filter((rId) => !relatedIds.includes(rId)));
        }
    };

    const handleRightCheck = (id) => {
        setSelectedRightIds((prev) =>
            prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]
        );
    };

    const handleProjectChange = (selectedProject) => {
        setTestRun({
            ...testRun,
            project: selectedProject
        })
        setProjectID(selectedProject.value)
    };

    const handleTestRunChange = (e) => {
        setTestRun({
            ...testRun,
            testRunName: e.target.value
        })
    };

    const handleTestRunDescriptionChange = (e) => {
        setTestRun({
            ...testRun,
            description: e.target.value
        })
    };

    const handleTestPlanChange = (selectedTestPlan) => {
        setTestRun({
            ...testRun,
            testPlan: selectedTestPlan
        })
    };

    const handleAssignChange = (selectedAssign) => {
        setTestRun({
            ...testRun,
            assignRun: selectedAssign
        })
    };

    const handleStateChange = (selectedState) => {
        setTestRun({
            ...testRun,
            state: selectedState
        })
    };

    const handleAddClick = () => {
        setOpen(true);
    };

    const handleSubmitTestCase = () => {
        setTestRun(prev => ({
            ...prev,
            testCases: `Test Cases : ${selectedRightIds.length}`
        }));
        setOpen(false);
    };

    const handleCancelTestCase = () => {
        setOpen(false);
    };

    const handleSaveTestRun = async () => {
        setCirleLoading(true);
        const userId = sessionStorage.getItem("currentUser");
        try {
            const payload = {
                name: testRun.testRunName,
                project: testRun.project.value,
                test_cases: selectedRightIds.map(tc => typeof tc === 'object' ? tc.id : tc),
                test_plan: testRun.testPlan.value,
                description: testRun.description,
                assigned_to: testRun.assignRun.value,
                state: testRun.state.value,
                created_by: userId
            };

            if (editData && editData.id) {
                await putData(`api/update_test_run/${editData.id}/`, payload);
            } else {
                await postData('api/testrun/', payload);
            }
            navigate(-1);
        } catch (error) {
            console.error("Error saving test run:", error);
        } finally {
            setCirleLoading(false);
        }
    }


    return (
        <DashboardLayout>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="sticky"
                    sx={{
                        backgroundColor: "#D7DEED",
                        borderRadius: '8px'
                    }}>
                    <Toolbar>
                        <MaterialTypography
                            variant="h5"
                            noWrap
                            fontWeight="bold"
                            color='dark'
                            sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }}
                        >
                            {editData ? 'Edit Test Run' : 'Create Test Run'}
                        </MaterialTypography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            <MaterialButton variant="outlined" color='dark' size="small" onClick={() => navigate(-1)}>Cancel</MaterialButton>
                            <MaterialButton variant='contained' color='dark' size='small' onClick={handleSaveTestRun}>Submit</MaterialButton>
                        </Box>
                    </Toolbar>
                </AppBar>
            </Box>
            <Box sx={{ padding: 2, mt: 2, backgroundColor: '#fff', borderRadius: '8px' }}>
                <Grid container spacing={2} direction='row'>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <MaterialBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <MaterialTypography variant='caption' color='secondary'>Project Name<span style={{ color: 'red' }}>*</span></MaterialTypography>
                            <MaterialSelect
                                label='Project Name'
                                size='small'
                                value={testRun.project}
                                options={projectList.map((p) => ({ label: p.label, value: p.value }))}
                                onChange={handleProjectChange}
                            />
                        </MaterialBox>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <MaterialBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <MaterialTypography variant='caption' color='secondary'>Test Run Name<span style={{ color: 'red' }}>*</span></MaterialTypography>
                            <MaterialInput
                                label='Test run Name'
                                size='small'
                                value={testRun.testRunName}
                                onChange={handleTestRunChange}
                            />
                        </MaterialBox>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <MaterialBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <MaterialTypography variant='caption' color='secondary'>Test Cases<span style={{ color: 'red' }}>*</span></MaterialTypography>
                            <Grid container direction='row' spacing={1}>
                                <Grid size={{ xs: 9.5 }}>
                                    <MaterialInput
                                        label='Test Cases'
                                        size='small'
                                        value={testRun.testCases}
                                    />
                                </Grid>
                                <Grid size={{ xs: 2 }}>
                                    <MaterialButton variant='contained' color='dark' size='small' onClick={handleAddClick}>Add</MaterialButton>
                                </Grid>
                            </Grid>
                        </MaterialBox>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <MaterialBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <MaterialTypography variant='caption' color='secondary'>Test Plans<span style={{ color: 'red' }}>*</span></MaterialTypography>
                            <MaterialSelect
                                label='Test Plans'
                                size='small'
                                value={testRun.testPlan}
                                options={testPlanList.map((test) => ({ label: test.label, value: test.value }))}
                                onChange={handleTestPlanChange}
                            />
                        </MaterialBox>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                        <MaterialBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <MaterialTypography variant='caption' color='secondary'>Assign Run</MaterialTypography>
                            <MaterialSelect
                                label='Assign Run'
                                size='small'
                                value={testRun.assignRun}
                                options={assignOptions.map((test) => ({ label: test.label, value: test.value }))}
                                onChange={handleAssignChange}
                            />
                        </MaterialBox>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <MaterialBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <MaterialTypography variant='caption' color='secondary'>State</MaterialTypography>
                            <MaterialSelect
                                label='Status'
                                size='small'
                                value={testRun.state}
                                options={stateOptions.map((test) => ({ label: test.label, value: test.value }))}
                                onChange={handleStateChange}
                            />
                        </MaterialBox>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12 }}>
                        <MaterialBox sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <MaterialTypography variant='caption' color='secondary'>Description</MaterialTypography>
                            <MaterialInput
                                label='Description'
                                placeholder='Write in brief about this test run'
                                size='small'
                                multiline
                                rows={4}
                                value={testRun.description}
                                onChange={handleTestRunDescriptionChange}
                            />
                        </MaterialBox>
                    </Grid>
                </Grid>
            </Box>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Select Test Cases</DialogTitle>

                <DialogContent className="dialog-content">
                    <div className="dialog-left">
                        <List dense>
                            {moduleList.map((item) => (
                                <ListItem key={item.id} disablePadding>
                                    <Checkbox
                                        checked={selectedLeftIds.includes(item.id)}
                                        onChange={() => handleLeftCheck(item.id)}
                                        size="small"
                                    />
                                    <ListItemText primary={item.label} />
                                </ListItem>
                            ))}
                        </List>
                    </div>

                    <div className="dialog-right">
                        <List dense>
                            {filteredRightList.length > 0 ? (
                                filteredRightList.map((item) => (
                                    <ListItem key={item.id} disablePadding>
                                        <Checkbox
                                            checked={selectedRightIds.includes(item.id)}
                                            onChange={() => handleRightCheck(item.id)}
                                            size="small"
                                        />
                                        <ListItemText primary={item.name} />
                                    </ListItem>
                                ))
                            ) : (
                                <ListItem>
                                    <ListItemText primary="No test cases available" />
                                </ListItem>
                            )}
                        </List>
                    </div>
                </DialogContent>

                <DialogActions>
                    <MaterialButton onClick={handleCancelTestCase} variant="outlined" color="dark" size='small'>
                        Cancel
                    </MaterialButton>
                    <MaterialButton onClick={handleSubmitTestCase} variant="contained" color="dark" size='small'>
                        Submit
                    </MaterialButton>
                </DialogActions>
            </Dialog>
        </DashboardLayout>
    )
}

export default CreateTestRun
